import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DISPLAY_MAX } from '../_lib/generationForms';
import type { GenerationForm } from '../_lib/useGenerationForm';

/**
 * «Год с» · «Год по» · «выпускается» and the live «В отчёте: …» preview of
 * the generation dialog (AUT-450 / VG-03). «выпускается» = `yearTo: null`
 * (the report reads «с 2021»). The preview is `formatGenerationDisplay` of
 * the form — red over `PICKER_REPORT_LIMITS.maxGenerationLength`.
 */
const K = 'vehicleCatalog.generations';
const YEAR_MAX_LENGTH = 4;

export const GenerationPreviewLine = ({ g }: { g: GenerationForm }): ReactElement => {
  const { t } = useTranslation();
  const over = g.preview?.over ?? false;
  return (
    <Box
      data-testid="generation-preview"
      data-over={over}
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: 1,
        borderColor: over ? 'error.main' : 'divider',
        color: over ? 'error.main' : 'text.primary',
      }}
    >
      <Typography variant="body2" color={over ? 'error' : 'text.secondary'}>
        {t(`${K}.preview.label`)}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ flex: 1, wordBreak: 'break-word' }}>
        {g.preview?.text ?? t(`${K}.preview.pending`)}
      </Typography>
      {g.preview ? (
        <Typography variant="caption" fontFamily="monospace">
          {t(`${K}.preview.counter`, { len: g.preview.length, max: DISPLAY_MAX })}
        </Typography>
      ) : null}
    </Box>
  );
};

const YearToField = ({
  g,
  disabled,
  inProduction,
}: {
  g: GenerationForm;
  disabled: boolean;
  inProduction: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const { control } = g.form;
  const yearOrder = g.serverError?.kind === 'yearOrder';
  return (
    <Controller
      control={control}
      name="yearTo"
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          size="small"
          label={t(`${K}.fields.yearTo`)}
          disabled={disabled || inProduction}
          onChange={(e) => {
            g.clearServerError('yearOrder');
            field.onChange(e.target.value);
          }}
          error={!inProduction && (yearOrder || fieldState.error !== undefined)}
          helperText={
            inProduction
              ? undefined
              : yearOrder
                ? t(`${K}.validation.yearOrder`)
                : fieldState.error?.message && t(fieldState.error.message)
          }
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: YEAR_MAX_LENGTH } }}
        />
      )}
    />
  );
};

export const GenerationYears = ({
  g,
  disabled,
}: {
  g: GenerationForm;
  disabled: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const { control } = g.form;
  const inProduction = g.form.watch('inProduction');
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 2, alignItems: 'start' }}>
      <Controller
        control={control}
        name="yearFrom"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            size="small"
            required
            label={t(`${K}.fields.yearFrom`)}
            disabled={disabled}
            onChange={(e) => {
              g.clearServerError('yearOrder');
              field.onChange(e.target.value);
              void g.form.trigger('yearTo');
            }}
            error={fieldState.error !== undefined}
            helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: YEAR_MAX_LENGTH } }}
          />
        )}
      />
      <YearToField g={g} disabled={disabled} inProduction={inProduction} />
      <Controller
        control={control}
        name="inProduction"
        render={({ field }) => (
          <FormControlLabel
            label={t(`${K}.fields.inProduction`)}
            disabled={disabled}
            control={
              <Checkbox
                checked={field.value}
                onChange={(e) => {
                  g.clearServerError('yearOrder');
                  field.onChange(e.target.checked);
                  void g.form.trigger('yearTo');
                }}
              />
            }
          />
        )}
      />
    </Box>
  );
};
